import type { PaginateProps, PaginateReturn, PaginateResponse, Response } from 'types';
import { Response as MirageResponse } from 'miragejs';
import { API_TRANSFORM_STORAGE_KEY, HTTP_RESPONSE_CODES } from 'mocks/constants';

type Attribs = {
  statusCode?: number;
  headers?: Record<string, string>;
  status?: string;
  code?: string;
  pagination?: { cursor: string; hasMore: boolean };
  error?: { details: { type: string; value: string }[] };
  body?: unknown | unknown[];
};

type Transform = {
  statusCode?: number;
  data?: unknown | unknown[];
  code?: string;
  error?: { details: { type: string; value: string }[] };
  url: string;
};

/**
 * Reads the active `apiTransform` override, if any (localStorage `api-transform`). Handlers
 * that write the response body via `attribs.body` (a raw passthrough) bypass `response()`'s own
 * transform application below, since that only applies to the `body` param — those handlers
 * must read the override and apply it by hand, and should use this shared reader rather than a
 * local copy (see items.ts / styles.ts / purchaseOrdersHelpers.ts generator-settings and
 * suppliers handlers).
 */
export const readApiTransform = (): Partial<Transform> | null => {
  try {
    const raw = localStorage.getItem('api-transform');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Reads a DEDICATED, opt-in `{ url, data }` override from a purpose-built localStorage key (not the
 * shared `api-transform`), returning `data` when the key is set and its `url` substring-matches the
 * request. Handlers that write their body via `attribs.body` (bypassing `response()`'s own
 * transform) and need a second, collision-free override channel use this — mirrors the inline
 * `getProcessTypesOverride`/`getProductCertGridOverride` readers in `purchaseOrdersMapping.ts`.
 * Returns null (leaving the handler byte-for-byte unchanged) when the key is absent.
 */
export const readNamespacedTransform = (
  storageKey: string,
  requestUrl: string
): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const transform = JSON.parse(raw) as { url?: string; data?: Record<string, unknown> };
    return transform?.url && requestUrl.includes(transform.url) && transform.data
      ? transform.data
      : null;
  } catch {
    return null;
  }
};

/**
 * Additive e2e seed reader (DL-64042 — Map suppliers shell specs): returns `defaults` merged with
 * an optional per-purchase-order mapping-count override written to the dedicated localStorage key
 * `e2e-map-suppliers-mapping-count` (`{ poId, data }`). This lets a shell spec drive deterministic
 * progress-bar / tab-dot states without consuming the single `api-transform` slot it needs for PO
 * status. Absent / different-PO / malformed seed → `defaults` returned unchanged, so no existing
 * handler behaviour changes. See `e2e/.../mapSuppliersShell.helpers.ts` (`seedMappingCount`).
 */
export const readMappingCountSeed = (
  poId: string,
  defaults: Record<string, unknown>
): Record<string, unknown> => {
  try {
    const seed = JSON.parse(localStorage.getItem('e2e-map-suppliers-mapping-count') || 'null') as {
      poId?: string | number;
      data?: Record<string, unknown>;
    } | null;
    if (seed?.data && (seed.poId == null || String(seed.poId) === poId)) {
      return { ...defaults, ...seed.data };
    }
  } catch {
    /* fall through to defaults */
  }
  return defaults;
};

/**
 * Composes the mapping-count handler's two additive, opt-in e2e overrides over `defaults`: the
 * DL-64042 per-PO seed (`readMappingCountSeed`) and the DL-63519 namespaced `warningFor` transform
 * (`readNamespacedTransform`, key `e2e-mapping-count-transform`). Absent both keys, unchanged.
 */
export const resolveMappingCountOverrides = (
  poId: string,
  requestUrl: string,
  defaults: Record<string, unknown>
): Record<string, unknown> => ({
  ...readMappingCountSeed(poId, defaults),
  ...(readNamespacedTransform('e2e-mapping-count-transform', requestUrl) ?? {}),
});

/**
 * Creates a mock API response with our standardized response format
 * @param {string} url - The URL endpoint for the response (request.url from mock server callback)
 * @param {unknown | unknown[]} body - The response body data (will be used in the data field of the response)
 * @param {Object} [attribs] - Optional attributes for customizing the response
 * @param {number} [attribs.statusCode] - HTTP status code for the response (defaults to 200)
 * @param {Record<string, string>} [attribs.headers] - Custom headers to include in the response (defaults to JSON content type)
 * @param {string} [attribs.status] - Response status ('success' or 'fail', success is default if statusCode is below 400)
 * @param {string} [attribs.code] - Response code (defaults to 'ok'), used to look up error message (see design doc for existing codes)
 * @param {string} [attribs.pagination.cursor] - Pagination cursor
 * @param {boolean} [attribs.pagination.hasMore] - Whether there are more pages
 * @param {Object} [attribs.error] - Error details (defaults to null)
 * @param {unknown | unknown[]} [attribs.body] - Custom response body (overrides the entire body), only used for special cases (and for support for old mocks)
 */
export const response = (url: string, body: unknown | unknown[], attribs?: Attribs) => {
  const current = localStorage.getItem(API_TRANSFORM_STORAGE_KEY);
  let transform: Transform = { url: '' };
  if (current) {
    try {
      transform = JSON.parse(current || '{}');
    } catch (error) {
      void 0;
    }
  }
  let status = 'success';
  if (attribs?.status) {
    status = attribs.status;
  } else if (attribs?.statusCode) {
    status = attribs.statusCode < 400 ? 'success' : 'fail';
  }
  let code = attribs?.code ?? 'ok';
  let error = attribs?.error ?? null;
  let statusCode = attribs?.statusCode ?? HTTP_RESPONSE_CODES.OK;
  let data = body;

  if (transform.url) {
    if (url.includes(transform.url)) {
      if (transform.statusCode) {
        statusCode = transform.statusCode;
      }
      if (transform.data) {
        data = transform.data;
      }
      if (transform.code) {
        code = transform.code;
      }
      if (transform.error) {
        error = transform.error;
      }
    }
  }

  if (attribs?.pagination) {
    const responseBody: PaginateResponse<unknown> = {
      status,
      code,
      data: Array.isArray(data) ? data : [data],
      pagination: attribs?.pagination ?? { cursor: '', hasMore: false },
      error,
      statusCode,
    };
    return new MirageResponse(statusCode, attribs?.headers ?? undefined, responseBody);
  }
  if (attribs?.body) {
    return new MirageResponse(statusCode, attribs?.headers ?? undefined, attribs?.body);
  }
  const responseBody: Response<unknown> = {
    status,
    code,
    data,
    error,
    statusCode,
  };
  return new MirageResponse(statusCode, attribs?.headers ?? undefined, responseBody);
};

export const paginate = <T>({
  items = [],
  perPage = 20,
  page = 1,
}: PaginateProps<T>): PaginateReturn<T> => {
  const matrix: T[][] = items.reduce<T[][]>((acc: T[][], item: T, index: number) => {
    const pageIndex = Math.floor(index / perPage);

    if (!acc[pageIndex]) {
      acc[pageIndex] = [];
    }

    acc[pageIndex].push(item);

    return acc;
  }, []);

  const validPage = page - 1 < matrix.length && page > 0 ? page - 1 : 0;
  const pageItems = matrix[validPage] || [];

  return {
    pageItems,
    pageSize: pageItems.length,
    numberOfPages: matrix.length,
  };
};

export const range = (len: number) => {
  const arr: number[] = [];
  for (let i = 0; i < len; i++) {
    arr.push(i);
  }
  return arr;
};

export function makeData<T>(len: number, fakeObject: (identifyer: number) => T) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(index),
    };
  });
}

export function makeChildData<T>(
  currentParentId: number,
  len: number,
  fakeObject: (parentId: number, identifyer: number) => T
) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(currentParentId, index),
    };
  });
}

export function makeFacilitiesData<T>(
  organizationSubSupplierId: string,
  len: number,
  fakeObject: (orgubSupplierId: string, identifyer: number) => T
) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(organizationSubSupplierId, index),
    };
  });
}
